/* eslint-disable max-len */
import {GameModeDataController} from '@colonist/shared-types'
import {MapDataController} from '../../../../../shared/map_data/MapDataController'
import {RoomClientState} from '../../../../shared/scripts/RoomClientState'
import {LobbyImage} from '@colonist/types'
import {clientI18n} from '../../../../shared/scripts/ClientI18n'
import {createDivIn, createHeaderIn, createImgIn, createLinkIn, createSpanIn, setElementOnClick} from '../../../../shared/scripts/ui_utils/DomUtils'
import {UIMRoomEditableCard} from './UIMRoomEditableCardView'
import {TranslationObject, getNestedTranslation} from '@colonist/i18n'
import {Sound} from '../../../../shared/scripts/Sound'
import {ViewPopupRulesInformation} from '../../../../shared/scripts/ui_popup/ViewPopupRulesInformation'
import {RoomSettingConstants} from '@colonist/game-base-types'
import {notificationController, socket} from '../../../../shared/scripts/EntryShared'
import {UIRoomSliderController} from '../../../../shared/scripts/ui_html/room/UIRoomSliderController'
import {UITopNotificationType} from '../../../../shared/scripts/notifications/UINotificationController'
import {Rule, RuleData, UIRoomSettingsRulesController} from '../../../../shared/scripts/ui_html/room/UIRoomSettingsRulesController'
import {UIRoomCenterSettingSpeedType, UIRoomCenterSettingsSpeedController} from '../../../../shared/scripts/ui_room/UIRoomCenterSettingsSpeedController'
import {UIRoomCenterSettingsMaxPlayersController} from '../../../../shared/scripts/ui_room/UIRoomCenterSettingsMaxPlayersController'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'

export class UIMRoomSettingsController {
    private readonly container: HTMLDivElement

    private gameModeCard: UIMRoomEditableCard
    private mapCard: UIMRoomEditableCard
    private membershipCTAContainer: HTMLDivElement

    private rulesHeader: HTMLHeadingElement
    private rulesInformationButton: HTMLLinkElement
    private rulesController: UIRoomSettingsRulesController

    private settingsContainer: HTMLDivElement

    private gameSpeedController: UIRoomCenterSettingsSpeedController
    private botSpeedController: UIRoomCenterSettingsSpeedController
    private maxPlayersController: UIRoomCenterSettingsMaxPlayersController

    private victoryPointsSlider: UIRoomSliderController
    private cardDiscardLimitSlider: UIRoomSliderController

    private shouldSyncPrivateRoomStateToServer: boolean = false
    private unSyncedPrivateRoomState: boolean = false
    private unSyncedNoTrollsState: boolean = false

    private readonly DEFAULT_SLIDER_STEP = 1

    private roomState: RoomClientState

    constructor(container: HTMLDivElement, roomState: RoomClientState, setActiveTab: (tabIdx: number) => void, shouldSyncPrivateRoomStateToServer: boolean = false) {
        this.container = container
        this.roomState = roomState
        this.shouldSyncPrivateRoomStateToServer = shouldSyncPrivateRoomStateToServer

        this.createEditableCards(setActiveTab)
        this.createMembershipCTA()
        this.createRules()
        this.createSettings()
        this.createSliders()

        this.setDisplay()
    }

    get isPrivateRoom(): boolean {
        if(this.shouldSyncPrivateRoomStateToServer) return this.roomState.gameSetting.privateGame
        return this.unSyncedPrivateRoomState
    }

    update(roomState: RoomClientState) {
        this.roomState = roomState
        const isViewOnly = this.isGuest()
        const gameModeData = GameModeDataController.getModeDataForMode(roomState.gameSetting.modeSetting)
        const mapData = MapDataController.getMapDataForMap(roomState.gameSetting.mapSetting)
        if(gameModeData == undefined || mapData == undefined) return

        const modeName = `Game mode: ${clientI18n.t(gameModeData.name.key, gameModeData.name.options)}`
        this.gameModeCard.update(modeName, gameModeData.icon, isViewOnly)

        const mapName = `Map: ${clientI18n.t(mapData.name.key, mapData.name.options)}`
        this.mapCard.update(mapName, mapData.image, isViewOnly)

        this.rulesController.update(roomState, this.getRulesData())
        this.gameSpeedController.update(roomState)
        this.botSpeedController.update(roomState)
        this.maxPlayersController.update(roomState)

        this.rulesController.updateInteractabilityOfElements(isViewOnly)
        this.gameSpeedController.updateInteractabilityOfElements(roomState)
        this.botSpeedController.updateInteractabilityOfElements(roomState)
        this.maxPlayersController.updateInteractabilityOfElements(roomState)

        const defaultVictoryPoints = GameModeDataController.getModeDataForMode(roomState.gameSetting.modeSetting)?.defaultVPsToWin ?? RoomSettingConstants.DEFAULT_VP_TO_WIN

        this.victoryPointsSlider.updateSliderConfig(defaultVictoryPoints, roomState.victoryPointsMaxAllowed)
        this.victoryPointsSlider.setWarningThreshold(roomState.victoryPointsRecommendedLimit)
        this.victoryPointsSlider.setValue(roomState.gameSetting.victoryPointsToWin, isViewOnly)

        this.cardDiscardLimitSlider.setValue(roomState.gameSetting.cardDiscardLimit, isViewOnly)
    }

    private createEditableCards(setActiveTab: (tabIdx: number) => void) {
        const isViewOnly = this.isGuest()
        const gameModeData = GameModeDataController.getModeDataForMode(this.roomState.gameSetting.modeSetting)
        const mapData = MapDataController.getMapDataForMap(this.roomState.gameSetting.mapSetting)

        if(gameModeData != undefined) {
            const modeName = getNestedTranslation({key: 'strings:game.settings.gameInfoTab.gameMode.value'}, {modeName: gameModeData.name})
            this.gameModeCard = new UIMRoomEditableCard(this.container, modeName, gameModeData.icon, () => setActiveTab(0), isViewOnly)
        }
        if(mapData != undefined) {
            const mapName = getNestedTranslation({key: 'strings:game.settings.gameInfoTab.map.value'}, {mapName: mapData.name})
            this.mapCard = new UIMRoomEditableCard(this.container, mapName, mapData.image, () => setActiveTab(1), isViewOnly)
        }
    }

    private createMembershipCTA() {
        this.membershipCTAContainer = createDivIn(this.container, 'room_center_become_a_member mobile-room')
        createSpanIn(this.membershipCTAContainer, '', {key: 'strings:roomPage.getAccessToAllMapsAndExpansions'})
        createLinkIn(this.membershipCTAContainer, '/store#membership', {key: 'strings:homePage.becomeAMember'}, 'btn_general btn_general_no_margin btn_general_orange btn-general-become-a-member', '', true)
    }

    private createRules() {
        const rulesContainer = createDivIn(this.container, 'room_center_options_settings')
        this.rulesHeader = createHeaderIn(rulesContainer, {key: 'strings:roomPage.rules.title'}, 'm-room-settings-section-header')

        this.rulesInformationButton = createLinkIn(this.rulesHeader, '', {key: 'strings:colonistCommon.utils.emptyString'}, 'm-room-settings-rules-information-button-link')
        this.rulesInformationButton.removeAttribute('href')
        createImgIn(this.rulesInformationButton, LobbyImage.InfoIcon, 'm-room-settings-rules-information-button-img')
        setElementOnClick(this.rulesInformationButton, () => {
            Sound.playSound(Sound.SoundType.Click)
            new ViewPopupRulesInformation()
        })

        const scrollContainer = createDivIn(rulesContainer, 'item-scroll')
        createImgIn(scrollContainer, LobbyImage.LeftArrow, 'scroll-arrow left-arrow')
        const scrollWrapper = createDivIn(scrollContainer, 'item-scroll-wrapper')
        createDivIn(scrollWrapper, 'item-scroll-container')
        createImgIn(scrollContainer, LobbyImage.LeftArrow, 'scroll-arrow right-arrow')

        this.rulesController = new UIRoomSettingsRulesController(scrollContainer)
        this.rulesController.load(this.roomState, this.getRulesData())
    }

    private getRulesData(): Map<Rule, RuleData> {
        const rulesData = new Map<Rule, RuleData>()

        rulesData.set(Rule.PrivateRoom, {
            selected: this.isPrivateRoom,
            onClick: () => this.handlePrivateRoomToggle(),
        })
        rulesData.set(Rule.NoTrolls, {
            available: !this.isNoTrollsDisabled(),
            selected: this.shouldSyncPrivateRoomStateToServer ? this.roomState.isNoTrollsGame() : this.unSyncedNoTrollsState,
            onClick: () => this.handleNoTrollsToggle(),
            onInfoClick: () => this.sendNoTrollsNotification(),
        })

        return rulesData
    }

    private createSettings() {
        this.settingsContainer = createDivIn(this.container, 'room_center_options_settings')
        createHeaderIn(this.settingsContainer, {key: 'strings:roomPage.options.advancedSettings.title'}, 'm-room-settings-section-header')
        const topSettingsContainer = createDivIn(this.settingsContainer, 'room_center_options')
        const bottomSettingsContainer = createDivIn(this.settingsContainer, 'room_center_options')

        const gameSpeedContainer = UIMRoomSettingsController.createSetting(topSettingsContainer, 'gamespeed_settings', {key: 'strings:roomPage.options.gameSpeed.title'})
        const botSpeedContainer = UIMRoomSettingsController.createSetting(topSettingsContainer, 'botspeed_settings', {key: 'strings:roomPage.options.botSpeed.title'})
        const maxPlayersContainer = UIMRoomSettingsController.createSetting(bottomSettingsContainer, 'maxplayers_settings', {key: 'strings:roomPage.options.maxPlayers.title'})

        this.gameSpeedController = new UIRoomCenterSettingsSpeedController(gameSpeedContainer, UIRoomCenterSettingSpeedType.GameSpeed)
        this.botSpeedController = new UIRoomCenterSettingsSpeedController(botSpeedContainer, UIRoomCenterSettingSpeedType.BotSpeed)
        this.maxPlayersController = new UIRoomCenterSettingsMaxPlayersController(maxPlayersContainer)

        this.gameSpeedController.load(this.roomState)
        this.botSpeedController.load(this.roomState)
        this.maxPlayersController.load(this.roomState)
    }

    private static createSetting(container: HTMLDivElement, id: string, header: TranslationObject): HTMLDivElement {
        const settingContainer = createDivIn(container, 'room_option_item', `room_center_${id}`)

        const titleContainer = createDivIn(settingContainer, 'option_title')
        createHeaderIn(titleContainer, header, '', 'H3')

        const infoButton = createImgIn(titleContainer, LobbyImage.InfoIcon, '', `${id}_speed_info`)
        infoButton.hidden = true

        const bodyContainer = createDivIn(settingContainer, 'option_body', `${id}_body`)
        createImgIn(bodyContainer, LobbyImage.LeftArrow, 'arrow-selector', `${id}_left_arrow`)
        createHeaderIn(bodyContainer, {key: 'strings:colonistCommon.utils.emptyString'}, '', `room_center_${id}_input`)
        createImgIn(bodyContainer, LobbyImage.LeftArrow, 'arrow-selector rotateimg180', `${id}_right_arrow`)

        return settingContainer
    }

    private createSliders() {
        const isViewOnly = this.isGuest()
        this.victoryPointsSlider = new UIRoomSliderController(this.settingsContainer,
            {key: 'strings:roomPage.options.vpToWin.title'},
            this.roomState.gameSetting.victoryPointsToWin,
            RoomSettingConstants.MIN_VP_TO_WIN,
            RoomSettingConstants.DEFAULT_VP_TO_WIN,
            this.DEFAULT_SLIDER_STEP,
            (victoryPoints: number) => socket.roomClient.setVictoryPointsToWin(victoryPoints),
            isViewOnly)

        this.cardDiscardLimitSlider = new UIRoomSliderController(this.settingsContainer,
            {key: 'strings:roomPage.options.cardDiscardLimit.title'},
            this.roomState.gameSetting.cardDiscardLimit,
            5,
            RoomSettingConstants.MAX_CARD_DISCARD_LIMIT,
            this.DEFAULT_SLIDER_STEP,
            (cardDiscard: number) => socket.roomClient.setCardDiscardLimit(cardDiscard),
            isViewOnly)
    }

    private setDisplay() {
        if(this.shouldSyncPrivateRoomStateToServer) {
            this.gameModeCard.show()
            this.mapCard.show()
            this.membershipCTAContainer.hidden = true
            return
        }

        this.gameModeCard.hide()
        this.mapCard.hide()
        this.membershipCTAContainer.hidden = ClientUserState.userState?.items.hasMembership() ?? false
    }

    setPrivateRoomStateSync(isActive: boolean) {
        this.shouldSyncPrivateRoomStateToServer = isActive
        if(!isActive) return
        socket.roomClient.setPrivateGame(this.unSyncedPrivateRoomState)

        const shouldToggleNoTrolls = this.roomState.isNoTrollsGame() != this.unSyncedNoTrollsState
        if(shouldToggleNoTrolls) socket.roomClient.toggleNoTrolls()
    }

    private handlePrivateRoomToggle() {
        if(this.shouldSyncPrivateRoomStateToServer) {
            socket.roomClient.setPrivateGame(!this.roomState.gameSetting.privateGame)
            return
        }
        Sound.playSound(Sound.SoundType.RoomSettingUpdated)

        this.unSyncedPrivateRoomState = !this.unSyncedPrivateRoomState
        this.rulesController.overrideRuleSelectedState(Rule.PrivateRoom, this.unSyncedPrivateRoomState)
        this.resetNoTrollsState()
    }

    private handleNoTrollsToggle() {
        if(this.isNoTrollsDisabled()) {
            this.sendNoTrollsNotification()
            return
        }

        if(this.shouldSyncPrivateRoomStateToServer) {
            this.handleNoTrollsToggleSynced()
            return
        }

        this.handleNoTrollsToggleUnSynced()
    }

    // eslint-disable-next-line class-methods-use-this
    private handleNoTrollsToggleSynced() {
        socket.roomClient.toggleNoTrolls()
    }

    private handleNoTrollsToggleUnSynced() {
        if(this.isPrivateRoom) {
            this.unSyncedNoTrollsState = false
            this.rulesController.overrideRuleAvailableState(Rule.NoTrolls, false)
            this.rulesController.overrideRuleSelectedState(Rule.NoTrolls, false)
            this.sendNoTrollsNotification()
            return
        }

        this.unSyncedNoTrollsState = !this.unSyncedNoTrollsState
        this.rulesController.overrideRuleAvailableState(Rule.NoTrolls, !this.isNoTrollsDisabled())
        this.rulesController.overrideRuleSelectedState(Rule.NoTrolls, this.unSyncedNoTrollsState)
    }

    private resetNoTrollsState() {
        if(this.unSyncedNoTrollsState) this.sendNoTrollsNotification()

        this.unSyncedNoTrollsState = false
        this.rulesController.overrideRuleAvailableState(Rule.NoTrolls, !this.isNoTrollsDisabled())
        this.rulesController.overrideRuleSelectedState(Rule.NoTrolls, this.unSyncedNoTrollsState)
    }

    private isGuest(): boolean {
        return this.roomState.isGuest(socket.controller.session.id)
    }

    private isNoTrollsDisabled(): boolean {
        if(!this.roomState.isHostSubscribed()) return true
        if(this.isPrivateRoom) return true
        return false
    }

    private sendNoTrollsNotification() {
        const message: TranslationObject = this.isPrivateRoom ? {key: 'strings:roomPage.notifications.noTrollsGameDisabledWhenPrivate'} : {key: 'strings:roomPage.options.noTrollsGame.becomeAMember'}
        notificationController.show(message, UITopNotificationType.Information, undefined, undefined, undefined, undefined, 3000)
    }
}
